/**
 * Data transformation utilities for frontend-backend integration
 */

import { ReferralCreate, Referral as BackendReferral } from './api';

// Frontend Referral interface (from App.tsx)
export interface FrontendReferral {
  id: string | number;
  referralNumber?: string;
  memberMedicaidId: string;
  memberName: string;
  phoneNumber?: string;
  email?: string;
  dateOfBirth: string;
  zipCode: string;
  preferredLanguage: string;
  streetAddress?: string;
  city?: string;
  state?: string;
  country?: string;
  healthPlan: string;
  planType: "Managed Care" | "Fee-for-Service";
  referringProvider: {
    name: string;
    npi: string;
    organization: string;
    phone: string;
  };
  primaryDiagnosis: string;
  riskScore?: number;
  sdohNeeds: string[];
  housingSubcategories?: string[];
  transportationSubcategories?: string[];
  urgencyLevel: "Immediate" | "Within 24h" | "Within 1 Week" | "Routine";
  availability?: string;
  additionalNotes?: string;
  authorizationRequired: boolean;
  consentObtained: boolean;
  hipaaCompliant: boolean;
  timeline: string;
  status: "Pending" | "Matched" | "Auto-Matched" | "Accepted" | "In Progress" | "On Hold" | "Completed";
  createdAt: string;
  timeAgo?: string;
  useMatchingEngine: boolean;
  // PHI Acceptance Workflow fields
  matchedProviderId?: string;
  acceptanceStatus?: 'pending' | 'accepted' | 'rejected' | 'expired';
  acceptedAt?: string;
  rejectedAt?: string;
  expiresAt?: string;
  phiEmailSent?: boolean;
  emailSentTo?: string;
  // Enrollment fields
  isEnrolled?: boolean;
  enrollmentId?: number;
  enrollmentStatus?: string;
  programName?: string;
  programId?: number;
  memberId?: number;
}

/**
 * Convert frontend referral to backend referral format
 */
export function frontendToBackendReferral(frontendReferral: FrontendReferral): ReferralCreate {
  // Use the COMPLETE format for database backend
  // The deployed backend now has full database schema support

  // SECURITY: Removed PHI logging for HIPAA compliance

  // Split member name into first and last
  const nameParts = (frontendReferral.memberName || '').trim().split(' ');
  const firstName = nameParts[0] || '';
  const lastName = nameParts.slice(1).join(' ') || '';

  // Complete format - database backend with full schema
  const result = {
    referral_number: frontendReferral.referralNumber,
    first_name: firstName,
    last_name: lastName,
    phone_number: frontendReferral.phoneNumber || '',
    email: frontendReferral.email || '',
    date_of_birth: frontendReferral.dateOfBirth,
    zip_code: frontendReferral.zipCode,
    preferred_language: frontendReferral.preferredLanguage,
    street_address: frontendReferral.streetAddress,
    city: frontendReferral.city,
    state: frontendReferral.state,
    country: frontendReferral.country,
    medicaid_id: frontendReferral.memberMedicaidId,
    health_plan: frontendReferral.healthPlan,
    plan_type: frontendReferral.planType,
    provider_name: frontendReferral.referringProvider.name,
    npi_number: frontendReferral.referringProvider.npi,
    organization: frontendReferral.referringProvider.organization,
    provider_phone_number: frontendReferral.referringProvider.phone,
    primary_diagnosis: frontendReferral.primaryDiagnosis,
    risk_score: frontendReferral.riskScore,
    availability: frontendReferral.availability || '',
    urgency_level: mapUrgencyLevel(frontendReferral.urgencyLevel),
    additional_notes: frontendReferral.additionalNotes || '',
    sdoh_needs: frontendReferral.sdohNeeds || [],
    housing_subcategories: frontendReferral.housingSubcategories || [],
    transportation_subcategories: frontendReferral.transportationSubcategories || [],
    prior_authorization_required: frontendReferral.authorizationRequired,
    member_consent_obtained: frontendReferral.consentObtained,
    hipaa_compliant: frontendReferral.hipaaCompliant,
    use_matching_engine: frontendReferral.useMatchingEngine,
    created_by: 1 // Default user ID
  };

  // SECURITY: Removed PHI logging for HIPAA compliance

  return result;
}

/**
 * Convert backend referral to frontend referral format
 */
export function backendToFrontendReferral(backendReferral: BackendReferral): FrontendReferral {
  // Always use complete format mapping for deployed backend
  // The deployed backend now uses the complete schema with all fields

  // SECURITY: Removed PHI logging for HIPAA compliance

  const memberName = `${backendReferral.first_name || ''} ${backendReferral.last_name || ''}`.trim();
  const fallbackMemberName = memberName || '';

  return {
    id: backendReferral.id || 0,
    referralNumber: backendReferral.referral_number,
    memberMedicaidId: backendReferral.medicaid_id || `MED${backendReferral.id || 0}`,
    memberName: fallbackMemberName,
    phoneNumber: backendReferral.phone_number,
    email: backendReferral.email,
    dateOfBirth: backendReferral.date_of_birth || '',
    zipCode: backendReferral.zip_code || '',
    preferredLanguage: backendReferral.preferred_language || "English",
    streetAddress: backendReferral.street_address || '',
    city: backendReferral.city || '',
    state: backendReferral.state || '',
    country: backendReferral.country || '',
    healthPlan: backendReferral.health_plan || '',
    planType: (backendReferral.plan_type as "Managed Care" | "Fee-for-Service") || "Managed Care",
    referringProvider: {
      name: backendReferral.provider_name || '',
      npi: backendReferral.npi_number || '',
      organization: backendReferral.organization || '',
      phone: backendReferral.provider_phone_number || '',
    },
    primaryDiagnosis: backendReferral.primary_diagnosis || '',
    riskScore: backendReferral.risk_score || 50,
    sdohNeeds: backendReferral.sdoh_needs || [],
    housingSubcategories: backendReferral.housing_subcategories || [],
    transportationSubcategories: backendReferral.transportation_subcategories || [],
    urgencyLevel: mapBackendUrgencyLevel(backendReferral.urgency_level || ''),
    availability: backendReferral.availability,
    additionalNotes: backendReferral.additional_notes,
    authorizationRequired: backendReferral.prior_authorization_required || false,
    consentObtained: backendReferral.member_consent_obtained || true,
    hipaaCompliant: backendReferral.hipaa_compliant || true,
    timeline: mapBackendUrgencyLevel(backendReferral.urgency_level || ''),
    status: mapBackendStatus(backendReferral.status || ''),
    createdAt: backendReferral.created_at || new Date().toISOString(),
    timeAgo: getTimeAgo(backendReferral.created_at || new Date().toISOString()),
    // PostgreSQL stores booleans properly, but handle any type coercion
    useMatchingEngine: backendReferral.use_matching_engine === true || (backendReferral.use_matching_engine as any) === 1,
    // PHI Acceptance Workflow fields
    matchedProviderId: (backendReferral as any).matched_provider_id,
    acceptanceStatus: (backendReferral as any).acceptance_status as 'pending' | 'accepted' | 'rejected' | 'expired' | undefined,
    acceptedAt: (backendReferral as any).accepted_at,
    rejectedAt: (backendReferral as any).rejected_at,
    expiresAt: (backendReferral as any).expires_at,
    phiEmailSent: (backendReferral as any).phi_email_sent === true || (backendReferral as any).phi_email_sent === 1,
    emailSentTo: (backendReferral as any).email_sent_to,
    // Enrollment fields
    isEnrolled: (backendReferral as any).is_enrolled === true || (backendReferral as any).is_enrolled === 1,
    enrollmentId: (backendReferral as any).enrollment_id,
    enrollmentStatus: (backendReferral as any).enrollment_status,
    programName: (backendReferral as any).program_name,
    programId: (backendReferral as any).program_id,
    memberId: (backendReferral as any).member_id,
  };
}

/**
 * Map frontend urgency levels to backend format
 */
function mapUrgencyLevel(frontendUrgency: string): string {
  const urgencyMap: Record<string, string> = {
    "Immediate": "Emergency",
    "Within 24h": "Urgent",
    "Within 1 Week": "Urgent",
    "Routine": "Routine",
  };
  return urgencyMap[frontendUrgency] || "Routine";
}

/**
 * Map backend urgency levels to frontend format
 */
function mapBackendUrgencyLevel(backendUrgency: string): "Immediate" | "Within 24h" | "Within 1 Week" | "Routine" {
  const urgencyMap: Record<string, "Immediate" | "Within 24h" | "Within 1 Week" | "Routine"> = {
    "Emergency": "Immediate",
    "Urgent": "Within 24h",
    "Routine": "Routine",
    "routine": "Routine", // Add lowercase mapping
  };
  return urgencyMap[backendUrgency || ''] || "Routine";
}

/**
 * Map backend status to frontend status
 */
function mapBackendStatus(backendStatus: string): "Pending" | "Matched" | "Auto-Matched" | "Accepted" | "In Progress" | "On Hold" | "Completed" {
  const statusMap: Record<string, "Pending" | "Matched" | "Auto-Matched" | "Accepted" | "In Progress" | "On Hold" | "Completed"> = {
    "pending": "Pending",
    "matched": "Matched",
    "Matched": "Matched", // Handle capital M
    "auto-matched": "Auto-Matched",
    "Auto-Matched": "Auto-Matched", // Handle capital A and M
    "accepted": "Accepted",
    "Accepted": "Accepted", // Handle capital A
    "in_progress": "In Progress",
    "in progress": "In Progress",
    "In Progress": "In Progress", // Handle capital I and P
    "on_hold": "On Hold",
    "on hold": "On Hold",
    "On Hold": "On Hold", // Handle capital O and H
    "completed": "Completed",
    "Completed": "Completed", // Handle capital C
  };
  const normalizedStatus = backendStatus?.toLowerCase() || '';
  const mappedStatus = statusMap[backendStatus] || statusMap[normalizedStatus] || "Pending";
  return mappedStatus;
}

/**
 * Calculate time ago string
 */
function getTimeAgo(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffInMs = now.getTime() - date.getTime();
  const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));
  
  if (diffInDays === 0) {
    return "Today";
  } else if (diffInDays === 1) {
    return "1 day ago";
  } else if (diffInDays < 7) {
    return `${diffInDays} days ago`;
  } else if (diffInDays < 30) {
    const weeks = Math.floor(diffInDays / 7);
    return `${weeks} week${weeks > 1 ? 's' : ''} ago`;
  } else {
    const months = Math.floor(diffInDays / 30);
    return `${months} month${months > 1 ? 's' : ''} ago`;
  }
}

/**
 * Generate a unique ID for frontend referrals
 */
export function generateReferralId(): string {
  return Math.random().toString(36).substring(2, 11);
}

/**
 * Create a new frontend referral from form data
 */
export function createFrontendReferral(formData: any): FrontendReferral {
  const now = new Date().toISOString();
  
  return {
    id: generateReferralId(),
    memberMedicaidId: formData.memberMedicaidId || `MED${generateReferralId()}`,
    memberName: formData.memberName || '',
    dateOfBirth: formData.dateOfBirth || '',
    zipCode: formData.zipCode || '',
    preferredLanguage: formData.preferredLanguage || 'English',
    healthPlan: formData.healthPlan || '',
    planType: formData.planType || 'Managed Care',
    referringProvider: {
      name: formData.providerName || '',
      npi: formData.npiNumber || '',
      organization: formData.organization || '',
      phone: formData.providerPhone || '',
    },
    primaryDiagnosis: formData.primaryDiagnosis || '',
    riskScore: formData.riskScore || undefined,
    sdohNeeds: formData.sdohNeeds || [],
    housingSubcategories: formData.housingSubcategories || [],
    transportationSubcategories: formData.transportationSubcategories || [],
    urgencyLevel: formData.urgencyLevel || 'Within 1 Week',
    authorizationRequired: formData.authorizationRequired || false,
    consentObtained: formData.consentObtained || false,
    hipaaCompliant: true,
    timeline: formData.urgencyLevel || 'Within 1 Week',
    status: 'Pending',
    createdAt: now,
    timeAgo: 'Just now',
    useMatchingEngine: formData.useMatchingEngine !== false,
  };
}
